import { cva } from 'class-variance-authority';
import type { CSSObjectWithLabel, StylesConfig } from 'react-select';

export const selectorWrapper = cva(['relative', 'flex', 'flex-col', 'gap-2', 'sf-selector']);

export const selectorLabel = cva([], {
  variants: {
    intent: {
      default: ['font-inter', 'text-body-responsive', 'font-medium', 'text-blue-900'],
      old: ['text-[14px]', 'font-normal', 'leading-normal', 'text-blue-700', 'font-trade'],
    },
    required: {
      true: ['after:content-["*"]', 'after:text-red-500'],
    },
  },
  defaultVariants: {
    intent: 'default',
  },
});

export const selectorErrorMessage = cva(['text-body-small text-red-500']);

interface SelectorStylesProps<Option, IsMulti extends boolean> {
  invalid?: boolean;
  customStyles?: Partial<StylesConfig<Option, IsMulti>>;
  isOldVersion?: boolean;
}

export const SelectorStyles = <Option, IsMulti extends boolean>({
  invalid = false,
  customStyles,
  isOldVersion = false,
}: SelectorStylesProps<Option, IsMulti>): StylesConfig<Option, IsMulti> => {
  const defaultStyles: StylesConfig<Option, IsMulti> = {
    menuList: (base: CSSObjectWithLabel) => ({
      ...base,
      '::-webkit-scrollbar': {
        width: '8px',
      },
      '::-webkit-scrollbar-thumb': {
        background: 'var(--color-grey-200)',
        borderRadius: '8px',
      },
      '::-webkit-scrollbar-thumb:hover': {
        background: 'var(--color-grey-200)',
      },
    }),
    menu: (base: CSSObjectWithLabel) => ({
      ...base,
      boxShadow: '0px 4px 14px 0px rgba(0, 0, 0, 0.12)',
      border: 'none',
      zIndex: 30,
    }),
    input: (base: CSSObjectWithLabel) => ({
      ...base,
      boxShadow: 'none',
      margin: 0,
      paddingBottom: 0,
      paddingTop: 0,
      lineHeight: 'var(--sf-select-line-height, 19.6px)',
      fontSize: 'var(--sf-select-font-size, 14px)',
    }),
    valueContainer: (base: CSSObjectWithLabel) => ({
      ...base,
      paddingTop: 0,
      paddingBottom: 0,
      minHeight: '24px',
    }),
    singleValue: (base: CSSObjectWithLabel) => ({
      ...base,
      lineHeight: 'var(--sf-select-line-height, 19.6px)',
      fontSize: 'var(--sf-select-font-size, 14px)',
      margin: 0,
    }),
    control: (base: CSSObjectWithLabel, state) => {
      return {
        ...base,
        boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
        background: state.isDisabled ? 'var(--color-grey-200)' : 'white',
        fontFamily: 'var(--font-inter)',
        color: state.isDisabled ? 'var(--color-grey-700)' : 'var(--color-grey-700)',
        border: invalid
          ? '1px solid var(--color-red-500)'
          : state.menuIsOpen
            ? '1px solid var(--color-blue-300)'
            : '1px solid var(--color-grey-400)',
        '&:hover': {
          border: invalid ? '1px solid var(--color-red-500)' : '1px solid inherit',
        },
        borderRadius: '6px',
        fontSize: 'var(--sf-select-font-size, 14px)',
        lineHeight: 'var(--sf-select-line-height, 19.6px)',
        minHeight: '42px',
        alignItems: 'center',
        margin: 0,
        cursor: state.isDisabled ? 'not-allowed' : 'pointer',
        flexWrap: 'nowrap',
        padding: '3px 12px 3px 2px',
        '&:active': {
          border: invalid ? '1px solid var(--color-red-500)' : '1px solid var(--color-blue-300)',
          outline: 'none',
        },
        '&:focus': {
          border: invalid ? '1px solid var(--color-red-500)' : '1px solid var(--color-blue-300)',
          outline: 'none',
        },
      };
    },
    placeholder: (base: CSSObjectWithLabel) => ({
      ...base,
      fontSize: 'var(--sf-select-font-size, 14px)',
      fontWeight: 400,
      fontFamily: 'var(--font-inter)',
      lineHeight: 'var(--sf-select-line-height, 19.6px)',
      color: 'var(--color-grey-700)',
    }),
    indicatorSeparator: (base: CSSObjectWithLabel) => ({
      ...base,
      display: 'none',
    }),
    indicatorsContainer: (base: CSSObjectWithLabel) => ({
      ...base,
      color: 'text-black',
    }),
    dropdownIndicator: (base: CSSObjectWithLabel, state) => ({
      ...base,
      color: state.isDisabled ? 'lightgray' : invalid ? 'text-error' : 'text-black',
    }),
  };

  if (isOldVersion) {
    defaultStyles.singleValue = (base: CSSObjectWithLabel) => ({
      ...base,
      color: '#757575',
      fontSize: '14px',
      fontWeight: 400,
      fontFamily: 'var(--font-trade-pro)',
      zIndex: 2,
    });
    defaultStyles.menu = (base: CSSObjectWithLabel) => ({
      ...base,
      border: 'none',
      marginTop: 0,
    });
    defaultStyles.menuList = (base: CSSObjectWithLabel) => ({
      ...base,
      borderBottomLeftRadius: '6px',
      borderBottomRightRadius: '6px',
      border: '1px solid var(--color-grey-700)',
      borderTop: 'none',
    });
    defaultStyles.control = (base: CSSObjectWithLabel) => ({
      ...base,
      border: 'none',
      color: '#757575',
      background: '#f5f5f5',
      padding: '0px 20px 0px 8px',
      boxShadow: 'none',
      borderColor: 'transparent',
      height: '56px',
      '&:hover': {
        borderColor: 'transparent',
      },
    });
    defaultStyles.input = (base: CSSObjectWithLabel) => ({
      ...base,
      border: 'none',
      color: '#757575',
      fontSize: '14px',
      fontWeight: 400,
      fontFamily: 'var(--font-trade-pro)',
      background: '#f5f5f5',
    });
  }

  return {
    ...defaultStyles,
    ...customStyles,
  };
};